import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {ActionBox} from 'shells/action-box/action-box';
import {ErrorIcon, SuccessIcon} from 'shells/action-box/action-icons';
import {Button} from 'shells/button/button';
import {Link} from 'shells/link/link';

import type {EditionFragment as Edition} from 'nutshell-graphql-types';

import {appHistory} from '../../../../history';

import {isMissingComparisonStreetAddress, isComparingContent} from '../a-b-testing/helpers';

import styles from './action-box.module.css';

type Props = {
    edition: Edition;
    onClickActionItem?: () => any;
    streetAddress: string | null | undefined;
    canAccessSetup: boolean;
};

export function StreetAddressActionBox(props: Props) {
    return (
        <ActionBox
            title='Mailing address'
            subtitle={getStreetAddressText(
                props.edition,
                props.streetAddress,
                props.canAccessSetup
            )}
            icon={
                props.streetAddress &&
                props.edition.hasStreetAddress &&
                !isMissingComparisonStreetAddress(props.edition) ? (
                    <SuccessIcon />
                ) : (
                    <ErrorIcon />
                )
            }
            actionElement={getStreetAddressActionElement(
                props.edition,
                props.streetAddress,
                props.canAccessSetup,
                props.onClickActionItem
            )}
        />
    );
}

function getStreetAddressText(
    edition: Edition,
    streetAddress: string | null | undefined,
    canAccessSetup: boolean
) {
    // No valid mailing address error should take precedence
    if (!streetAddress) {
        if (canAccessSetup) {
            return 'Please add a mailing address in settings';
        }

        return 'Ask your administrator to add a mailing address in their company settings';
    }

    if (isComparingContent(edition)) {
        if (!edition.hasStreetAddress || isMissingComparisonStreetAddress(edition)) {
            return (
                <>
                    Each version needs a <code>{'{mailingAddress}'}</code> merge tag
                </>
            );
        }
    } else if (!edition.hasStreetAddress) {
        return (
            <>
                All messages need a <code>{'{mailingAddress}'}</code> merge tag
            </>
        );
    }

    return streetAddress;
}

function getStreetAddressActionElement(
    edition: Edition,
    streetAddress: string | null | undefined,
    canAccessSetup: boolean,
    onClickAction?: () => any
) {
    // If there is no street address configured, users with setup access can add a new address
    if (!streetAddress && canAccessSetup) {
        return (
            <div className='flex' style={{minWidth: 'fit-content'}}>
                {/* @ts-expect-error */}
                <Link variant='primary' as={RoutedLink} to='/setup/index?addressFocus=1'>
                    Add address in settings
                </Link>
            </div>
        );
    }

    // If the street address is configured but merge tag is missing from edition
    if ((streetAddress && !edition.hasStreetAddress) || isMissingComparisonStreetAddress(edition)) {
        if (onClickAction) {
            return (
                <Button variant='text-primary' size='big' onClick={onClickAction}>
                    <code>Learn how to add</code>
                </Button>
            );
        }

        return (
            <div className={styles['button-container']}>
                <Button
                    variant='text-primary'
                    size='big'
                    onClick={() => {
                        NutClientConfig.shouldShowAddressModal = true;

                        appHistory.push(`${edition.htmlUrlPath}/editor`);
                    }}
                >
                    <code>Learn how to add</code>
                </Button>
            </div>
        );
    }

    // Even if everything is fine, users with setup access always have the option to edit the address
    if (canAccessSetup) {
        return (
            <div className='flex' style={{minWidth: 'fit-content'}}>
                {/* @ts-expect-error */}
                <Link variant='primary' as={RoutedLink} to='/setup/index?addressFocus=1'>
                    Edit in settings
                </Link>
            </div>
        );
    }
}
